/** @jsx jsx */
import React, { createContext, useEffect, useState } from 'react'
import get from 'lodash/get'
import { jsx } from '@emotion/react'
import { Prompt } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'

import Portal from './Portal'

export interface ExtraFormContextInterface {
  autoSubmitEnabled?: boolean
  onSubmit?: (data: any) => void
  formIsDisabled?: boolean
}

export const ExtraFormContext = createContext<ExtraFormContextInterface | null>(
  null
)

export interface Props {
  /** Component that needs to be passed in by the project. */
  FormSuccess?: any
  /** Component that needs to be passed in by the project. */
  FormErrorCtx?: any
  /** Component that needs to be passed in by the project. */
  FormErrorsHint?: any
  /** If true, each input will trigger the handleSubmit callback onBlur */
  autoSubmitEnabled?: boolean
  /** If true, success and form error messages will be centered */
  centerMessages?: boolean
  /** If true, the form will prevent the user from navigating away while the form is dirty */
  dataLossWarningEnabled?: boolean
  /** Use this to override the default message with a custom one. */
  dataLossWarningMessage?: string
  /** If true, the entire form shall be disabled. useIsDisabled will make use of this information. */
  formIsDisabled?: boolean
  /** Use this if you need to shift the error message out of the form layout */
  formMsgPortalId?: string
  /** If true, the form is inline and doesn't need 100% width */
  inline?: boolean
  /** Use this to override the hint that appears when any form errors are present. Will not render anything if empty string is provided. */
  errorMessage?: string
  /** Make sure you prepared the formCtx with toSnakeCase and transform (if needed) */
  formCtx?: any
  /** The mutate function. If you don't pass this in, pass in submitHandler instead. */
  mutate?: any
  /** The data from your mutation. We need this to extract the form errors. Must be passed in if you pass in mutate. */
  mutationData?: any
  /** The mutation name. */
  mutationName?: string
  /** <Form> always renders an invisible <input type="submit" /> so that ENTER key always works. If you want to disable this, use this prop. */
  noSubmitElement?: string
  /** Callback so that you can react to form changes. */
  onChange?: (res: React.FormEvent<HTMLFormElement>) => void
  /** Callback so that the form component can do some action after a successful submit. */
  onSuccess?: (res: any) => void
  /** Optional prefix, in case we use form-groups in Django with form prefixes */
  prefix?: string
  /** List of field-names that should not trigger auto-submits */
  preventAutoSubmitFor?: string[]
  /** Callback function to be called on submit. If you don't pass this in, pass in mutate and mutationName instead. */
  submitHandler?: (formData: any) => void
  /** Use this to override the success message that appears when the status is 200. */
  successMessage?: string
  /** Resets the isSubmitSuccess state after this many milliseconds. */
  successTimeout?: number
  /** Pass this in so that we can transform the formValues just before submitting them. */
  transformReverse?: (formValues: any) => any
  /** Pass this in if you need to transform the raw formData into something else that will be used for the form reset after success. */
  transformResetTo?: (formValues: any) => any
  /** Used to check for special mobile styles */
  isMobile?: boolean
  children: React.ReactNode
}

const noTransform = (values: any) => values

const Form: React.FC<Props> = (props) => {
  const {
    FormErrorCtx,
    FormErrorsHint,
    FormSuccess,
    autoSubmitEnabled,
    centerMessages,
    children,
    dataLossWarningEnabled,
    dataLossWarningMessage = 'You have unsaved form changes. Do you really want to leave this page?',
    errorMessage = 'Some fields have errors. Please fix the fields marked in red.',
    formCtx,
    formIsDisabled,
    formMsgPortalId,
    inline,
    isMobile,
    mutate,
    mutationData,
    mutationName,
    noSubmitElement,
    onChange,
    onSuccess,
    prefix,
    preventAutoSubmitFor = [],
    submitHandler,
    successMessage = 'Saved Successfully',
    successTimeout,
    transformReverse = noTransform,
    transformResetTo = noTransform,
  } = props
  if (!mutate && !submitHandler) {
    throw new Error('Please pass in either mutate or submitHandler')
  }

  if (!!mutate && !mutationName) {
    throw new Error('Please pass in mutationData and mutationName')
  }

  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false)
  const { formState, handleSubmit } = formCtx
  const { isDirty } = formState

  // if autoSubmit is enabled, we need to register a form-field to keep track
  // of which input caused the auto-submit. It is the responsibility of each
  // input to set this field to the input-name when the autoSubmit happens.
  // usually, you would want to do something like this in the onBlur of
  // the input-component:
  // ```
  // if (!!canAutoSubmit) {
  //   ctx.setValue('auto_submit', name)
  //   onSubmit(ctx.getValues())
  // }
  // ```
  const registerAutoSubmit = () => {
    if (!!autoSubmitEnabled) {
      formCtx.register('auto_submit')
      formCtx.setValue('auto_submit', '')
    }
  }

  useEffect(() => {
    registerAutoSubmit()
  }, [])

  // Keeping track if the user has changed any form values but not submitted
  useEffect(() => {
    if (!!dataLossWarningEnabled) {
      window.onbeforeunload = () => {
        if (!!isDirty) {
          return dataLossWarningMessage
        }
        return
      }
    }
  }, [isDirty])

  // Keeping track of form errors, if the mutation returns any,
  // we add them to the react-hook-forms context via setError
  // so that all fields can know that they have errors
  const formErrorsStr =
    get(mutationData, `${mutationName}.formErrors`, '{}') || '{}'
  useEffect(() => {
    const formErrors = JSON.parse(formErrorsStr)
    Object.keys(formErrors).forEach((key) => {
      const error =
        typeof formErrors[key] === 'string'
          ? formErrors[key]
          : formErrors[key][0]
      formCtx.setError(key, { message: error })
    })
  }, [formErrorsStr])

  /**
   * On submit we do a lot of things:
   * 1. we reverse the transformation, if needed
   * 2. we set submitSuccess to false just before we execute the mutation
   * 3. we set submitSuccess to true if the mutation returns with status 200
   * 4. if it was a successful submit, we reset the form, so that dirty is false again
   * 5. if it is an auto-submit form, we re-register the auto_submit field
   */
  const onSubmit = async (data: any) => {
    let formData
    if (!!transformReverse) {
      formData = { ...transformReverse(data) }
    } else {
      formData = { ...data }
    }

    let resetTo = { ...data }
    if (!!transformResetTo) {
      resetTo = transformResetTo(resetTo)
    }

    const formDataStr = JSON.stringify(formData)

    const resetForm = (success: boolean) => {
      if (!!success) {
        formCtx.reset(resetTo)
      } else {
        formCtx.reset(resetTo, { errors: true })
      }
      registerAutoSubmit()
    }

    if (!!mutate) {
      setIsSubmitSuccess(false)
      await mutate({ variables: { data: formDataStr } }).then((res: any) => {
        if (get(res, `data.${mutationName}.status`) === 200) {
          setIsSubmitSuccess(true)
          resetForm(true)
          if (!!successTimeout) {
            setTimeout(() => {
              setIsSubmitSuccess(false)
            }, successTimeout)
          }
          if (!!onSuccess) {
            onSuccess(res)
          }
        } else {
          resetForm(false)
        }
      })
    }

    if (!!submitHandler) {
      submitHandler(formData)
      resetForm(true)
    }
  }

  const renderFormMessages = () => {
    const formMessages = (
      <React.Fragment>
        {!!isSubmitSuccess && (
          <FormSuccess
            css={{
              textAlign: !!centerMessages ? 'center' : 'inherit',
            }}
          >
            {successMessage}
          </FormSuccess>
        )}
        <FormErrorCtx
          name={`${!!prefix ? `${prefix}-` : ''}__all__`}
          centered={!!centerMessages}
          renderHiddenInput={true}
        />
        {!!errorMessage && errorMessage !== '' && (
          <FormErrorsHint
            errorMessage={errorMessage}
            css={{ textAlign: !!centerMessages ? 'center' : 'inherit' }}
          />
        )}
      </React.Fragment>
    )
    if (!formMsgPortalId) {
      return formMessages
    }
    return <Portal id={formMsgPortalId}>{formMessages}</Portal>
  }

  return (
    <ExtraFormContext.Provider
      value={{ autoSubmitEnabled, formIsDisabled, onSubmit }}
    >
      {!!dataLossWarningEnabled && (
        <Prompt when={isDirty} message="You have unsaved changes." />
      )}
      <FormProvider {...formCtx}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          onChange={(e) => {
            if (!!onChange) {
              onChange(e)
            }
          }}
          css={{
            width: inline ? 'auto' : '100%',
            display: inline ? 'inline-block' : 'block',
            height: isMobile ? '100%' : undefined,
          }}
        >
          {children}
          {renderFormMessages()}
          {!noSubmitElement && (
            <input type="submit" style={{ display: 'none' }} />
          )}
        </form>
      </FormProvider>
    </ExtraFormContext.Provider>
  )
}

export default Form
